import { createDebugLogger } from "@evidenceb/debug-utils";
import last from "lodash/last.js";
import sortBy from "lodash/sortBy.js";
import { addRxPlugin, } from "rxdb";
import { disableWarnings, RxDBDevModePlugin } from "rxdb/plugins/dev-mode";
import { RxDBLeaderElectionPlugin } from "rxdb/plugins/leader-election";
import { RxDBLocalDocumentsPlugin, } from "rxdb/plugins/local-documents";
import { RxDBMigrationPlugin } from "rxdb/plugins/migration-schema";
import { replicateRxCollection, } from "rxdb/plugins/replication";
import { exhaustMap, filter, firstValueFrom, interval, map, } from "rxjs";
import { AthenaEventSaveErrorReason } from "../AthenaEventSaveError/AthenaEventSaveErrorReason.js";
import { createDatabaseWithDm5Migration } from "../utils/dbMigration.js";
import { isPreprodOrLocal, isTest, shouldEnableRxdbDevMode, } from "../utils/envCheck.js";
import { athenaEventSchemaV0 } from "./athenaEventSchema.js";
import { AthenaEventStreamNotStarted, LastSyncMarkerWriteError, ReplicationPullError, ReplicationPushNonRetryableError, ReplicationPushRetryableError, ReplicationPushWrongBatchSizeError, SubscriberAlreadyRegistered, SubscriberNotRegistered, SubscriberProcessAthenaEventError, } from "./errors.js";
import { LAST_SYNC_INFO_LOCAL_DOC_ID, } from "./LastSyncInfo.js";
import { MIGRATION_CUTOFF_LOCAL_DOC_ID, } from "./MigrationCutoff.js";
import { fromAthenaEvent, toAthenaEvent, } from "./RxdbAthenaEvent.js";
const debug = createDebugLogger("athena-event-stream");
if (shouldEnableRxdbDevMode()) {
    disableWarnings();
    addRxPlugin(RxDBDevModePlugin);
}
addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);
/**
 * Entity responsible for orchestrating the flow of `AthenaEvent`s from
 * publishers to subscribers.
 *
 * Publishers are those components that _create_ `AthenaEvent`s, by calling the
 * `AthenaEventStream.publish` method.
 *
 * Subscribers are those components that _receive and process_ `AthenaEvent`s.
 * Subscribers are "hooked" to `AthenaEventStream` with the
 * `AthenaEventStream.registerSubscriber` method.
 */
export class AthenaEventStream {
    eventStorageClient;
    userId;
    hooks;
    pullAthenaEventTypes;
    pullInterval;
    retryFailedPushOrPullAfter;
    insertSubscriptionsBySubscriberId = new Map();
    rxdb = null;
    /**
     * AthenaEvents that existed before an RxDB storage migration ran are
     * skipped during push (see `MigrationCutoff.ts`). Set once in
     * `startReplication`, before replication is started.
     */
    migrationCutoff = null;
    /**
     * Subscription to `replicationState.active$`, set up in
     * `startReplication`. Unsubscribed in `stop`, before the database is
     * removed, so it can't end up calling `awaitInSync` (and the rest of the
     * replication machinery) against a database that's concurrently being torn
     * down.
     */
    replicationActiveSubscription = null;
    /**
     * When true, the push handler short-circuits with a retryable error so no
     * events are sent to the backend, while local `publish` keeps queueing them
     * in RxDB (same guarantee as being offline). Toggled by the app while a
     * blocking popup — idle or expired session — is shown, so events aren't
     * pushed against a dead token; RxDB resumes pushing once unpaused.
     */
    paused = false;
    lastSyncInfoDoc = null;
    __instanceId = Math.random().toString(36).slice(2, 8);
    /**
     * DO NOT USE:
     * `AthenaEventStream` instance has not been started.
     */
    __rxdb = null;
    __replicationState = null;
    /**
     * Breakdown (in milliseconds) of how long each sub-step of
     * `start()` took, exposed for performance diagnostics. Null until `start()`
     * has completed. Lets callers attribute the event-stream start-up wait to
     * its real driver (IndexedDB/RxDB creation vs. initial replication pull vs.
     * local-doc bookkeeping)
     */
    __startTimings = null;
    constructor(eventStorageClient, 
    /** Id of the logged in user. */
    userId, 
    /** Hooks to be notified when notable events (e.g., errors) occur. */
    hooks, 
    /**
     * List of `AthenaEventType`s to pull from the event-storage service. Since
     * many `AthenaEvent`s are just for tracking what the user does, and the
     * frontend is not interested in keeping them around, we're probably only
     * interested in "syncing back" a subset of `AthenaEvent`s.
     */
    pullAthenaEventTypes, 
    /**
     * Interval at which to pull from the event-storage service.
     * @default 30_000
     */
    pullInterval = 30_000, 
    /**
     * Time after which to retry a failed pull or push.
     * @default 5_000
     */
    retryFailedPushOrPullAfter = 5_000) {
        this.eventStorageClient = eventStorageClient;
        this.userId = userId;
        this.hooks = hooks;
        this.pullAthenaEventTypes = pullAthenaEventTypes;
        this.pullInterval = pullInterval;
        this.retryFailedPushOrPullAfter = retryFailedPushOrPullAfter;
        debug.info("instance created", {
            instanceId: this.__instanceId,
            userId: this.maskUserId(),
        });
    }
    maskUserId() {
        return this.userId.length <= 6
            ? this.userId
            : `${this.userId.slice(0, 3)}...${this.userId.slice(-3)}`;
    }
    async start() {
        debug.info("start() begin", {
            instanceId: this.__instanceId,
        });
        const startedAt = performance.now();
        await this.createDatabase();
        const createdDbAt = performance.now();
        // startReplication records its own sub-step timings into __startTimings;
        // we fill in the createDb/total figures around it here.
        await this.startReplication();
        const finishedAt = performance.now();
        this.__startTimings = {
            total: Math.round(finishedAt - startedAt),
            createDb: Math.round(createdDbAt - startedAt),
            migration: this.__startTimings?.migration ?? 0,
            buildReplication: this.__startTimings?.buildReplication ?? 0,
            initLastSyncDoc: this.__startTimings?.initLastSyncDoc ?? 0,
            replicationStart: this.__startTimings?.replicationStart ?? 0,
        };
        debug.info("start() done", {
            instanceId: this.__instanceId,
            timings: this.__startTimings,
        });
    }
    async stop() {
        this.ensureStarted(this.rxdb);
        debug.info("stop() begin", {
            instanceId: this.__instanceId,
        });
        // Cancel replication and unsubscribe before removing the database:
        // otherwise the replication machinery (in particular the awaitInSync()
        // call below in startReplication's active$ subscription) can still be
        // running against a database that's concurrently being torn down,
        // producing spurious errors and, if a new AthenaEventStream is started
        // for the same user right after, racing it for access to the same
        // underlying storage.
        this.replicationActiveSubscription?.unsubscribe();
        this.lastSyncInfoDoc = null;
        await this.__replicationState?.cancel();
        debug.info("stop() replication cancelled", {
            instanceId: this.__instanceId,
        });
        await this.rxdb.remove();
        debug.info("stop() done, database removed", {
            instanceId: this.__instanceId,
        });
    }
    /**
     * Pauses or resumes pushing events to the backend. Pausing does NOT stop
     * local publishing: events keep being queued in RxDB and are flushed once
     * resumed (same behaviour as going offline). See the `paused` field.
     */
    setPaused(paused) {
        if (this.paused === paused)
            return;
        this.paused = paused;
        console.info("[athena-event-stream] setPaused()", {
            instanceId: this.__instanceId,
            paused,
        });
        if (paused)
            this.__replicationState?.pause();
        else
            this.__replicationState?.start();
    }
    /** Publishes an `AthenaEvent` to all registered subscribers. */
    publish(athenaEvent) {
        this.ensureStarted(this.rxdb);
        debug.info("publish()", {
            instanceId: this.__instanceId,
            id: athenaEvent.id,
            type: athenaEvent.type,
        });
        this.rxdb.athena_events.insert(fromAthenaEvent(AthenaEventStream.cloneAthenaEvent(athenaEvent)));
    }
    /**
     * Registers a subscriber. `AthenaEvent`s published before the call to
     * `registerSubscriber` are not processed by the subscriber (from its
     * point of view, it's like they never existed). Only `AthenaEvent`s published
     * after the call to `registerSubscriber` are processed by the subscriber.
     */
    registerSubscriber(subscriber) {
        if (this.insertSubscriptionsBySubscriberId.has(subscriber.id)) {
            throw new SubscriberAlreadyRegistered(subscriber.id);
        }
        this.ensureStarted(this.rxdb);
        const insertSubscription = this.rxdb.athena_events.insert$.subscribe((insertEvent) => {
            this.processAthenaEvent(AthenaEventStream.cloneAthenaEvent(toAthenaEvent(insertEvent.documentData)), subscriber);
        });
        this.insertSubscriptionsBySubscriberId.set(subscriber.id, insertSubscription);
        return this;
    }
    /**
     * Un-registers a subscriber. `AthenaEvent`s published after the call to
     * `unregisterSubscriber` are not processed by the subscriber anymore.
     */
    unregisterSubscriber(subscriberId) {
        const insertSubscription = this.insertSubscriptionsBySubscriberId.get(subscriberId);
        if (!insertSubscription) {
            throw new SubscriberNotRegistered(subscriberId);
        }
        this.insertSubscriptionsBySubscriberId.delete(subscriberId);
        insertSubscription.unsubscribe();
        return this;
    }
    processAthenaEvent(athenaEvent, subscriber) {
        try {
            if (!AthenaEventStream.athenaEventMatchesFilter(athenaEvent, subscriber.athenaEventTypeFilter)) {
                return;
            }
            subscriber.processAthenaEvent(athenaEvent, this);
        }
        catch (error) {
            this.hooks?.onSubscriberProcessAthenaEventError?.(new SubscriberProcessAthenaEventError(error, athenaEvent, subscriber.id));
        }
    }
    /** Finds `AthenaEvent`s by the supplied query. */
    async find(selector = {}) {
        this.ensureStarted(this.rxdb);
        const docs = await this.rxdb.athena_events
            .find({ selector, sort: [{ createdAt: "asc" }] })
            .exec();
        return docs.map(toAthenaEvent);
    }
    async findWithTimings(selector = {}) {
        this.ensureStarted(this.rxdb);
        const rawStart = performance.now();
        const docs = await this.rxdb.athena_events
            .find({ selector, sort: [{ createdAt: "asc" }] })
            .exec();
        const rawEnd = performance.now();
        const convertStart = performance.now();
        const events = docs.map(toAthenaEvent);
        const convertEnd = performance.now();
        return {
            events,
            timings: {
                query: Math.round(rawEnd - rawStart),
                convert: Math.round(convertEnd - convertStart),
                count: events.length,
                dev_mode: shouldEnableRxdbDevMode(),
                validated_storage: isPreprodOrLocal(),
            },
        };
    }
    /**
     * Returns an rxjs Observable emitting the latest result of the supplied query
     * (an array of `AthenaEvent`s).
     */
    find$(selector = {}) {
        this.ensureStarted(this.rxdb);
        return this.rxdb.athena_events
            .find({ selector, sort: [{ createdAt: "asc" }] })
            .$.pipe(map((athenaEventDocuments) => athenaEventDocuments.map((athenaEventDocument) => toAthenaEvent(athenaEventDocument.toJSON()))));
    }
    /**
     * Returns a promise that resolves:
     *
     * - Right-away if the last performed sync was done less than `delta`
     *   milliseconds ago.
     * - Whenever another sync is performed otherwise.
     */
    async waitForRecentSync(delta) {
        this.ensureStarted(this.rxdb);
        await firstValueFrom(this.rxdb.athena_events
            .getLocal$(LAST_SYNC_INFO_LOCAL_DOC_ID)
            .pipe(filter((lastSync) => lastSync !== null &&
            lastSync.get("lastSyncedAt") > Date.now() - delta)));
    }
    static athenaEventMatchesFilter(athenaEvent, filter) {
        if (typeof filter === "string") {
            return filter === athenaEvent.type;
        }
        if (Array.isArray(filter)) {
            return filter.includes(athenaEvent.type);
        }
        if (typeof filter === "function") {
            return filter(athenaEvent.type);
        }
        return true;
    }
    static cloneAthenaEvent(athenaEvent) {
        return JSON.parse(JSON.stringify(athenaEvent));
    }
    /**
     * Whether `athenaEvent` predates the last RxDB storage migration (if any)
     * and can be assumed to already have reached the backend, so the push for
     * it can be skipped without making a network call. See
     * `MigrationCutoff.ts` for why this is needed: the replication
     * bookkeeping that would otherwise tell RxDB "this was already pushed"
     * doesn't survive the storage migration, so without this check every
     * AthenaEvent that existed before migration would get pushed again.
     */
    shouldSkipPushForMigratedEvent(athenaEvent) {
        return (this.migrationCutoff !== null &&
            athenaEvent.createdAt <= this.migrationCutoff.createdAt);
    }
    isPushOnlyEventType(athenaEventType) {
        if (!this.pullAthenaEventTypes) {
            return false;
        }
        return !this.pullAthenaEventTypes.includes(athenaEventType);
    }
    /**
     * Called once we know `athenaEvent` is on the backend: the push actually
     * succeeded, the backend confirmed it already has it (`UniqueViolation`),
     * or the push was skipped entirely because it predates the migration
     * cutoff. Bounds local storage by removing the local copy of push-only
     * AthenaEvent types (never pulled back, so they serve no further purpose
     * once we know the backend has them). Deliberately NOT called when a push
     * fails non-retryably for any other reason — in that case the backend has
     * explicitly rejected the AthenaEvent, so the local copy is the only one
     * that will ever exist and must not be pruned. Failures here are caught
     * and logged rather than thrown, so a failed prune can never prevent the
     * push from being marked resolved.
     */
    async onAthenaEventConfirmedSynced(athenaEvent) {
        if (!this.isPushOnlyEventType(athenaEvent.type)) {
            return;
        }
        try {
            const localDoc = await this.rxdb?.athena_events
                .findOne(athenaEvent.id)
                .exec();
            await localDoc?.remove();
        }
        catch (error) {
            console.warn("[athena-event-stream] failed to prune push-only AthenaEvent after resolution", { id: athenaEvent.id, type: athenaEvent.type, error });
        }
    }
    async createDatabase() {
        const databaseName = `db_${this.userId}`;
        debug.info("createRxDatabase() begin", {
            userId: this.maskUserId(),
            databaseName,
            isTest: isTest(),
        });
        try {
            this.__rxdb = this.rxdb = await createDatabaseWithDm5Migration({
                databaseName,
                collections: {
                    athena_events: {
                        schema: athenaEventSchemaV0,
                        localDocuments: true,
                    },
                },
                maskedUserId: this.maskUserId(),
            });
            debug.info("createRxDatabase() done", {
                userId: this.maskUserId(),
                databaseName,
            });
        }
        catch (error) {
            console.error("[athena-event-stream] createDatabase() failed", {
                userId: this.maskUserId(),
                databaseName,
                error,
            });
            throw error;
        }
    }
    async startReplication() {
        this.ensureStarted(this.rxdb);
        const readCutoffStart = performance.now();
        const migrationCutoffDoc = await this.rxdb.athena_events.getLocal(MIGRATION_CUTOFF_LOCAL_DOC_ID);
        this.migrationCutoff = migrationCutoffDoc
            ? { createdAt: migrationCutoffDoc.get("createdAt") }
            : null;
        const buildReplicationStart = performance.now();
        const replicationIdentifier = [
            "athena_events_replication",
            this.userId,
            this.eventStorageClient.eventStorageOrigin,
            this.pullAthenaEventTypes?.join("_") ?? "ALL",
        ].join("_");
        debug.info("startReplication()", {
            instanceId: this.__instanceId,
            replicationIdentifier,
            migrationCutoff: this.migrationCutoff,
        });
        const replicationState = replicateRxCollection({
            collection: this.rxdb.athena_events,
            replicationIdentifier,
            retryTime: this.retryFailedPushOrPullAfter,
            push: {
                // If pushing a batch fails, when RxDB retries the push, it pushes again
                // the entire batch. On the other hand, when we try to save a batch of
                // `AthenaEvent`s to `EvidenceB/event-storage`, some `AthenaEvent`s
                // might get saved successfully, while others don't. In that case there
                // would be no way of telling RxDB to only retry pushing those
                // `AthenaEvent`s that failed. This would be problematic in the case in
                // which a batch contains an `AthenaEvent` that fails saving due to a
                // non-retryable error. Subsequent attempts at saving the batch would
                // keep failing because of that "rogue" `AthenaEvent`, and the whole
                // batch would never be marked as replicated by RxDB.
                batchSize: 1,
                handler: async (docs) => {
                    if (docs.length !== 1) {
                        const error = new ReplicationPushWrongBatchSizeError(docs.length);
                        this.hooks?.onReplicationPushWrongBatchSizeError?.(error);
                        throw error;
                    }
                    const [doc] = docs;
                    // We currently have no use case of local deletion except for push only events.
                    // In that specific case, we need to _not_ replicate the deletion
                    if (doc.newDocumentState._deleted) {
                        debug.info("push skipped: local deletion", {
                            instanceId: this.__instanceId,
                            id: doc.newDocumentState.id,
                        });
                        return [];
                    }
                    const athenaEvent = toAthenaEvent(doc.newDocumentState);
                    debug.info("push handler invoked", {
                        instanceId: this.__instanceId,
                        id: athenaEvent.id,
                        type: athenaEvent.type,
                        createdAt: athenaEvent.createdAt,
                        isLeader: this.rxdb?.isLeader(),
                        documentHidden: typeof document !== "undefined" ? document.hidden : undefined,
                    });
                    // AthenaEvents that existed before an RxDB storage migration are
                    // assumed to already have reached the backend (see
                    // `MigrationCutoff.ts`), so we skip the network call for them
                    // entirely rather than re-verifying with the backend.
                    if (this.shouldSkipPushForMigratedEvent(athenaEvent)) {
                        debug.info("push skipped: predates migration cutoff", {
                            instanceId: this.__instanceId,
                            id: athenaEvent.id,
                        });
                        await this.onAthenaEventConfirmedSynced(athenaEvent);
                        return [];
                    }
                    const [result] = (await this.eventStorageClient.saveAthenaEvents([
                        athenaEvent,
                    ]));
                    debug.info("push handler result", {
                        instanceId: this.__instanceId,
                        id: athenaEvent.id,
                        type: athenaEvent.type,
                        succeeded: result.succeeded,
                        errorReason: result.error?.reason,
                        retryable: result.error?.retryable,
                    });
                    if (result.error) {
                        if (result.error.retryable) {
                            const error = new ReplicationPushRetryableError(result);
                            this.hooks?.onReplicationPushRetryableError?.(error);
                            throw error;
                        }
                        //Event was already pushed
                        if (result.error.reason === AthenaEventSaveErrorReason.UniqueViolation) {
                            this.hooks?.onReplicationPushUniqueViolation?.({
                                athenaEvent,
                                instanceId: this.__instanceId,
                                isLeader: this.rxdb?.isLeader(),
                                documentHidden: typeof document !== "undefined" ? document.hidden : undefined,
                            });
                            await this.onAthenaEventConfirmedSynced(athenaEvent);
                            return [];
                        }
                        const error = new ReplicationPushNonRetryableError(result);
                        this.hooks?.onReplicationPushNonRetryableError?.(error);
                        // It's pointless to retry saving `AthenaEvent`s that failed
                        // saving with a non-retryable error. Hence we tell RxDB to mark
                        // the `AthenaEvent` as replicated and stop trying to save it.
                        // Note: unlike UniqueViolation, this means the backend
                        // explicitly rejected the AthenaEvent, so we don't prune the
                        // local copy — it's the only copy that will ever exist.
                        return [];
                    }
                    await this.onAthenaEventConfirmedSynced(athenaEvent);
                    return [];
                },
            },
            pull: {
                // batchSize is set to be very large, so we expect all new
                // `AthenaEvent`s to be pulled at once.
                batchSize: 100_000,
                handler: async (lastCheckpoint) => {
                    try {
                        const athenaEvents = await this.eventStorageClient.fetchAthenaEvents({
                            userId: this.userId,
                            athenaEventTypes: this.pullAthenaEventTypes,
                            insertedAfter: lastCheckpoint
                                ? new Date(lastCheckpoint.insertedAt)
                                : undefined,
                        });
                        const lastInserted = last(sortBy(athenaEvents, "insertedAt", "id"));
                        return {
                            documents: athenaEvents.map(fromAthenaEvent),
                            checkpoint: lastInserted
                                ? {
                                    id: lastInserted.id,
                                    insertedAt: lastInserted.insertedAt,
                                }
                                : lastCheckpoint,
                        };
                    }
                    catch (cause) {
                        const error = new ReplicationPullError(cause);
                        this.hooks?.onReplicationPullError?.(error);
                        throw error;
                    }
                },
                // Sets an interval to periodically pull new `AthenaEvent`s.
                stream$: interval(this.pullInterval).pipe(map(() => "RESYNC")),
            },
            // We start the replication manually just a few lines above, to ensure
            // that the lastSync local doc is set up before it starts.
            autoStart: false,
        });
        const initLastSyncStart = performance.now();
        // Create a local document keeping track of when he last sync was performed,
        // and update it on each sync.
        this.lastSyncInfoDoc = await this.rxdb.athena_events
            .insertLocal(LAST_SYNC_INFO_LOCAL_DOC_ID, {
            // Set to any date in the past (epoch)
            lastSyncedAt: 0,
        })
            .catch(() => null);
        if (!this.lastSyncInfoDoc) {
            this.lastSyncInfoDoc =
                await this.rxdb.athena_events.getLocal(LAST_SYNC_INFO_LOCAL_DOC_ID);
        }
        const subscribeStart = performance.now();
        this.replicationActiveSubscription = replicationState.active$
            .pipe(
        // Only react when replication goes idle: active$ also emits `true`
        // when a pull/push cycle starts, which we don't need to act on.
        filter((active) => !active), exhaustMap(async () => {
            try {
                await replicationState.awaitInSync();
            }
            catch (cause) {
                const error = new ReplicationPullError(cause);
                this.hooks?.onReplicationPullError?.(error);
                return;
            }
            try {
                this.lastSyncInfoDoc = this.lastSyncInfoDoc
                    ? await this.lastSyncInfoDoc.incrementalPatch({
                        lastSyncedAt: Date.now(),
                    })
                    : await this.rxdb.athena_events.upsertLocal(LAST_SYNC_INFO_LOCAL_DOC_ID, { lastSyncedAt: Date.now() });
            }
            catch (cause) {
                const error = new LastSyncMarkerWriteError(cause, {
                    instanceId: this.__instanceId,
                    isLeader: this.rxdb?.isLeader(),
                    documentHidden: typeof document !== "undefined" ? document.hidden : undefined,
                });
                this.hooks?.onLastSyncMarkerWriteError?.(error);
                this.lastSyncInfoDoc = null;
            }
        }))
            .subscribe({
            error: (cause) => {
                const error = new ReplicationPullError(cause);
                this.hooks?.onReplicationPullError?.(error);
            },
        });
        const replicationStartStart = performance.now();
        await replicationState.start();
        const replicationStartEnd = performance.now();
        this.__replicationState = replicationState;
        // Stash sub-step timings; start() reads these back and fills in the
        // createDb/total figures. `subscribeStart..replicationStartStart` (the
        // synchronous active$ subscription setup) is negligible and folded into
        // buildReplication implicitly by not being measured separately.
        this.__startTimings = {
            total: 0, // overwritten by start()
            createDb: 0, // overwritten by start()
            migration: Math.round(buildReplicationStart - readCutoffStart),
            buildReplication: Math.round(initLastSyncStart - buildReplicationStart),
            initLastSyncDoc: Math.round(subscribeStart - initLastSyncStart),
            replicationStart: Math.round(replicationStartEnd - replicationStartStart),
        };
        // If the stream was paused before replication finished starting, honour it.
        if (this.paused)
            replicationState.pause();
    }
    ensureStarted(rxdb) {
        if (!rxdb) {
            throw new AthenaEventStreamNotStarted();
        }
    }
}
//# sourceMappingURL=AthenaEventStream.js.map